/**
 * Sales hub shell. Routes: /dashboard (overview), /sales/skus, /sales/brands, /sales/platforms
 * (App.js renders <SalesHubPage view="sku|brand|platform" />).
 *
 * Owns the one scope (useSalesScope, kept in the URL) for every view and renders, in order:
 *   sub-navigation tabs (links keep the scope query string; "Daily records" carries period + filters),
 *   then the view:
 *     undefined | 'overview' -> <SalesOverviewPage salesScope={...} />  (renders its own header,
 *                               control bar and Upload button because its upload also moves the period)
 *     'sku' | 'brand' | 'platform' -> header here (title, period line, Upload sales for editors),
 *                               one SalesControlBar here, then <SalesSkusPage|SalesBrandsPage|
 *                               SalesPlatformsPage scope={scope} setScope={setScope} /> (the page body).
 * Passing scope/setScope makes the view pages skip their own header (usePageScope -> salesScope null),
 * so every page has exactly one control bar. Refresh / a finished upload remounts the view to refetch.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { canEditModule, viewOnlyHint } from '../lib/permissions';
import SalesOverviewPage from './SalesOverviewPage';
import SalesSkusPage from './SalesSkusPage';
import SalesBrandsPage from './SalesBrandsPage';
import SalesPlatformsPage from './SalesPlatformsPage';
import { SalesViewTabs } from '../components/sales/overview/SalesViewTabs';
import { ImportNotice, SalesPageHeader, importMessage } from '../components/sales/overview/SalesPageHeader';
import { SalesControlBar } from '../components/sales/SalesControlBar';
import { SalesUploadDialog } from '../components/sales/SalesUploadDialog';
import { useSalesScope } from '../components/sales/useSalesScope';
import { useSalesOptions } from '../components/sales/views/common';
import '../styles/sales-hub.css';

const VIEWS = {
  sku: { Page: SalesSkusPage, title: 'SKU performance', testId: 'sales-skus-heading' },
  brand: { Page: SalesBrandsPage, title: 'Brands & categories', testId: 'sales-brands-heading' },
  platform: { Page: SalesPlatformsPage, title: 'Platform performance', testId: 'sales-platforms-heading' },
};

export default function SalesHubPage({ view = 'overview' }) {
  const key = VIEWS[view] ? view : 'overview';
  const salesScope = useSalesScope();
  const { scope, setScope } = salesScope;
  const { user } = useAuth();
  const canEditSales = canEditModule(user, 'sales');
  const isView = key !== 'overview';
  // Catalog-wide brand / category lists for the control bar (the overview gets them from its own hub call).
  const options = useSalesOptions(scope.country, isView);
  const [reloads, setReloads] = useState(0);
  const [message, setMessage] = useState('');
  const reload = () => setReloads(value => value + 1);
  const refresh = () => { setMessage(''); reload(); };
  // The upload message describes the last import only: clear it on a view, period or filter change.
  const scopeKey = JSON.stringify(scope);
  useEffect(() => { setMessage(''); }, [key, scopeKey]);

  const imported = summary => {
    setMessage(importMessage(summary));
    reload();
  };

  let body;
  if (!isView) {
    body = <SalesOverviewPage salesScope={salesScope} />;
  } else {
    const { Page, title, testId } = VIEWS[key];
    body = (
      <>
        <SalesPageHeader salesScope={salesScope} title={title} testId={testId}>
          {canEditSales
            ? <SalesUploadDialog onImported={imported} />
            : <span className="self-center text-xs text-gray-600" data-testid="sales-view-only">View only · {viewOnlyHint(user)}</span>}
        </SalesPageHeader>
        <SalesControlBar salesScope={salesScope} brands={options.brands} categories={options.categories} onRefresh={refresh} />
        <ImportNotice message={message} onDismiss={() => setMessage('')} />
        <Page key={`${key}-${reloads}`} scope={scope} setScope={setScope} />
      </>
    );
  }

  return (
    <div className="sales-hub space-y-5" data-testid="sales-hub-page">
      <SalesViewTabs view={key} scope={scope} />
      {body}
    </div>
  );
}
